import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import {
  LogIn, LogOut, PauseCircle, PlayCircle,
  CheckCircle2, AlertTriangle, Clock, ChevronRight,
  History, Download, ShieldCheck, Info, Smartphone, Trash2
} from "lucide-react";
import moment from "moment";
import "moment/locale/ca";
import {
  getJourneyState, getSuggestedActions, calcWorkedMinutes,
  minutesToHHMM, getTypeLabel, STATE_CONFIG, ACTION_CONFIG, JOURNEY_STATES
} from "@/components/time/journeyEngine";
import QuickLoader from "@/components/shared/QuickLoader";
import PWAInstallPrompt from "@/components/time/PWAInstallPrompt";
import WasteMicroForm from "@/components/waste/WasteMicroForm";

moment.locale("ca");

const TYPE_ICONS = { entry: LogIn, exit: LogOut, pause_start: PauseCircle, pause_end: PlayCircle };
const ACTION_TYPE_COLORS = {
  entry: "text-emerald-600 bg-emerald-50",
  exit: "text-rose-600 bg-rose-50",
  pause_start: "text-amber-600 bg-amber-50",
  pause_end: "text-blue-600 bg-blue-50",
};

export default function WorkerApp() {
  const { currentVenue, user, loading: venueLoading } = useVenue();
  const [now, setNow] = useState(moment());
  const [todayRecords, setTodayRecords] = useState([]);
  const [submitting, setSubmitting] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showLegal, setShowLegal] = useState(false);
  const [workedLive, setWorkedLive] = useState(0);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [showInstall, setShowInstall] = useState(false);
  const [showWaste, setShowWaste] = useState(false);

  // Clock ticker
  useEffect(() => {
    const t = setInterval(() => setNow(moment()), 1000);
    return () => clearInterval(t);
  }, []);

  // Load records
  useEffect(() => {
    if (!currentVenue || !user) return;
    loadTodayRecords();
    // Check if we should show install prompt (first visit)
    const installed = localStorage.getItem("hosys_pwa_dismissed");
    if (!installed) setTimeout(() => setShowInstall(true), 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVenue?.id, user?.email]);

  // Live worked counter
  useEffect(() => {
    const activeRecords = todayRecords.filter(r => !r.is_deleted);
    const t = setInterval(() => {
      setWorkedLive(calcWorkedMinutes(activeRecords));
    }, 15000);
    return () => clearInterval(t);
  }, [todayRecords, calcWorkedMinutes]);

  useEffect(() => {
    setWorkedLive(calcWorkedMinutes(todayRecords.filter(r => !r.is_deleted)));
  }, [todayRecords, calcWorkedMinutes]);

  const loadTodayRecords = async () => {
    setLoadingRecords(true);
    const today = moment().format("YYYY-MM-DD");
    const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");
    const recs = await hosys.entities.TimeRecord.filter({ venue_id: currentVenue.id, user_email: user.email });
    const yesterdayRecs = recs.filter(r => r.date === yesterday && !r.is_deleted);
    const hasOpenEntryYesterday = (() => {
      const sorted = [...yesterdayRecs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      if (!sorted.length) return false;
      const last = sorted[sorted.length - 1];
      return last.type === "entry" || last.type === "pause_start" || last.type === "pause_end";
    })();
    const todayRecs = recs.filter(r => r.date === today);
    const combined = hasOpenEntryYesterday ? [...yesterdayRecs, ...todayRecs] : todayRecs;
    setTodayRecords(combined.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
    setLoadingRecords(false);
  };

  const handleClock = async (type) => {
    if (submitting) return;
    setSubmitting(type);
    const ts = new Date().toISOString();
    await hosys.entities.TimeRecord.create({
      venue_id: currentVenue.id,
      user_email: user.email,
      user_name: user.full_name || user.email,
      type,
      timestamp: ts,
      date: moment().format("YYYY-MM-DD"),
      source: "pwa",
    });
    setConfirmation({ type, time: moment().format("HH:mm") });
    setTimeout(() => setConfirmation(null), 4000);
    await loadTodayRecords();
    setSubmitting(null);
  };

  if (venueLoading || loadingRecords) return <QuickLoader />;

  const activeRecords = todayRecords.filter(r => !r.is_deleted);
  const journey = getJourneyState(activeRecords);
  const { primary, secondary } = getSuggestedActions(journey);
  const cfg = journey.state !== JOURNEY_STATES.NOT_STARTED ? STATE_CONFIG[journey.state] : null;

  const ACTION_VISUAL = {
    entry: { label: "ENTRADA", color: "bg-emerald-500 active:bg-emerald-600 shadow-emerald-500/40" },
    pause_start: { label: "PAUSA", color: "bg-amber-400 active:bg-amber-500 shadow-amber-400/40" },
    pause_end: { label: "FI PAUSA", color: "bg-blue-500 active:bg-blue-600 shadow-blue-500/40" },
    exit: { label: "SORTIDA", color: "bg-rose-500 active:bg-rose-600 shadow-rose-500/40" },
  };

  const ALL_SECONDARY = ["entry", "pause_start", "pause_end", "exit"].filter(t => t !== primary && t !== secondary);

  return (
    <div className="min-h-screen bg-[#f5f0f1] flex flex-col">
      {/* Status bar spacer for iOS PWA */}
      <div className="h-safe-top bg-[#7B2D3F]" style={{ height: "env(safe-area-inset-top, 0px)", background: "#7B2D3F" }} />

      {/* Header */}
      <div className="bg-[#7B2D3F] text-white px-5 pt-4 pb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-white/60 text-xs font-medium capitalize">{now.format("dddd, D MMMM")}</p>
            <p className="text-white font-bold text-base leading-tight mt-0.5">{user?.full_name?.split(" ")[0]}</p>
            <p className="text-white/50 text-xs">{currentVenue?.name}</p>
          </div>
          <div className="text-right">
            <p className="text-4xl font-bold font-mono tabular-nums tracking-tight leading-none">
              {now.format("HH:mm")}
            </p>
            <p className="text-white/40 font-mono text-xs text-right">{now.format(":ss")}</p>
          </div>
        </div>

        {/* Journey status pill */}
        {cfg && (
          <div className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2">
            <span className={`w-2 h-2 rounded-full animate-pulse ${cfg.dot}`} />
            <span className="text-sm font-semibold text-white">{cfg.label}</span>
            {workedLive > 0 && (
              <span className="ml-auto text-white/70 text-sm font-mono font-bold">{minutesToHHMM(workedLive)}</span>
            )}
            {journey.entryTime && (
              <span className="text-white/50 text-xs">· Entrada {moment(journey.entryTime).format("HH:mm")}</span>
            )}
          </div>
        )}
        {!cfg && (
          <div className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2">
            <span className="w-2 h-2 rounded-full bg-gray-400" />
            <span className="text-sm text-white/70">Torn no iniciat</span>
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 px-4 py-5 space-y-4 pb-32" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 6rem)" }}>

        {/* Confirmation banner */}
        {confirmation && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3 shadow-sm">
            <div className="w-11 h-11 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="font-bold text-emerald-800 text-base">{ACTION_CONFIG[confirmation.type]?.confirmMsg}</p>
              <p className="text-emerald-600 text-sm">a les {confirmation.time} · {currentVenue?.name}</p>
            </div>
          </div>
        )}

        {/* Issues */}
        {journey.issues.length > 0 && !confirmation && (
          <div className="space-y-2">
            {journey.issues.map((issue, i) => (
              <div key={`issue-${i}`} className={`flex items-center gap-2.5 rounded-xl p-3 border text-sm font-medium ${issue.severity === "error" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {issue.msg}
              </div>
            ))}
          </div>
        )}

        {/* Inconsistent */}
        {journey.state === JOURNEY_STATES.INCONSISTENT && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5 text-center">
            <AlertTriangle className="w-9 h-9 text-red-500 mx-auto mb-2" />
            <p className="font-bold text-red-800 text-base">Jornada amb incidències</p>
            <p className="text-sm text-red-600 mt-1">Contacta amb el supervisor per corregir el registre.</p>
          </div>
        )}

        {/* PRIMARY ACTION — Big tap button */}
        {journey.state !== JOURNEY_STATES.INCONSISTENT && primary && (
          <button
            onClick={() => handleClock(primary)}
            disabled={!!submitting}
            className={`w-full ${ACTION_VISUAL[primary].color} text-white rounded-3xl py-7 flex flex-col items-center gap-2 transition-all active:scale-95 disabled:opacity-50 shadow-2xl`}
            style={{ minHeight: 120 }}
          >
            <span className="font-black text-3xl tracking-widest">{ACTION_VISUAL[primary].label}</span>
            {submitting === primary && <span className="text-sm text-white/70">Registrant...</span>}
          </button>
        )}

        {/* SECONDARY ACTION */}
        {journey.state !== JOURNEY_STATES.INCONSISTENT && secondary && (
          <button
            onClick={() => handleClock(secondary)}
            disabled={!!submitting}
            className="w-full bg-white border-2 border-gray-200 text-gray-700 rounded-2xl py-5 flex items-center justify-center gap-2 transition-all active:scale-95 font-bold text-lg"
          >
            {ACTION_VISUAL[secondary]?.label || secondary}
          </button>
        )}

        {/* Compact other actions */}
        {journey.state !== JOURNEY_STATES.INCONSISTENT && (
          <div className="grid grid-cols-2 gap-3">
            {ALL_SECONDARY.map(type => {
              const v = ACTION_VISUAL[type];
              if (!v) return null;
              return (
                <button
                  key={type}
                  onClick={() => handleClock(type)}
                  disabled={!!submitting}
                  className="bg-white border border-gray-200 text-gray-500 rounded-2xl py-4 flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-30 font-semibold text-sm opacity-60"
                >
                  {v.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Today's records */}
        {activeRecords.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full px-4 py-3 flex items-center justify-between border-b border-gray-50"
            >
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-gray-400" />
                <p className="text-sm font-semibold text-gray-700">Registres d'avui</p>
                <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">{activeRecords.length}</span>
              </div>
              <div className="flex items-center gap-2">
                {workedLive > 0 && <span className="text-xs font-bold text-[#7B2D3F]">{minutesToHHMM(workedLive)}</span>}
                <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${showHistory ? "rotate-90" : ""}`} />
              </div>
            </button>
            {showHistory && (
              <div className="divide-y divide-gray-50">
                {activeRecords.map(r => {
                  const Icon = TYPE_ICONS[r.type] || LogIn;
                  return (
                    <div key={r.id} className="flex items-center gap-3 px-4 py-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${ACTION_TYPE_COLORS[r.type]}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium text-gray-700 flex-1">{getTypeLabel(r.type)}</span>
                      <span className="text-sm font-mono font-bold text-gray-800">{moment(r.timestamp).format("HH:mm")}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* PWA Install card (inline) */}
        <div
          className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3 cursor-pointer active:bg-gray-50 transition-colors"
          onClick={() => setShowWaste(true)}
          data-testid="worker-waste-card"
        >
          <div className="w-11 h-11 bg-[#7B2D3F]/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <Trash2 className="w-5 h-5 text-[#7B2D3F]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-800 text-sm">Registrar merma</p>
            <p className="text-xs text-gray-400 mt-0.5">Registra menjar malbé en menys de 10s</p>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
        </div>

        {/* PWA Install card (inline) */}
        <div
          className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3 cursor-pointer active:bg-gray-50 transition-colors"
          onClick={() => setShowInstall(true)}
        >
          <div className="w-11 h-11 bg-[#7B2D3F]/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-5 h-5 text-[#7B2D3F]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-800 text-sm">Instal·la Ho-Sys al mòbil</p>
            <p className="text-xs text-gray-400 mt-0.5">Accés ràpid amb una sola icona</p>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
        </div>

        {/* Legal */}
        <div className="flex items-center justify-center gap-1.5 pt-1">
          <button onClick={() => setShowLegal(!showLegal)} className="flex items-center gap-1 text-[10px] text-gray-300 hover:text-gray-400 transition-colors">
            <ShieldCheck className="w-3 h-3" />
            Registre oficial RD-Llei 8/2019
            <Info className="w-3 h-3" />
          </button>
        </div>
        {showLegal && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-700">
            Conforme al <strong>Reial Decret-llei 8/2019</strong>, tots els fitxatges es registren de forma fiable amb data, hora i treballador. Es conserven durant <strong>4 anys</strong>.
          </div>
        )}
      </div>

      {/* PWA Install Modal */}
      {showInstall && (
        <PWAInstallPrompt onClose={() => {
          localStorage.setItem("hosys_pwa_dismissed", "1");
          setShowInstall(false);
        }} />
      )}

      {/* Waste Micro Form */}
      {showWaste && currentVenue && (
        <WasteMicroForm
          venueId={currentVenue.venue_id || currentVenue.id}
          onClose={() => setShowWaste(false)}
        />
      )}
    </div>
  );
}